import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CaretLeft,
  CheckCircle,
  ShieldCheck,
  Spinner,
  Warning,
} from "@phosphor-icons/react";
import useCreditLimitEligibilityStatus from "../hooks/useCreditLimitEligibilityStatus";
import useClaimCreditLimit from "../hooks/useClaimCreditLimit";

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(value);

/**
 * Claim page for an assigned credit limit. Reached from the "Claim Your Amount"
 * button in the credit-limit-approved email. Shows the assigned amount as a
 * preview and lets the user claim it, which creates a loan request for review.
 */
const ClaimCreditLimit = () => {
  const navigate = useNavigate();
  const {
    creditLimitEligibilityStatus: status,
    isLoading,
    isError,
    refetch,
  } = useCreditLimitEligibilityStatus();
  const { claimCreditLimitAsync, claiming } = useClaimCreditLimit();

  const [claimed, setClaimed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const creditLimit = Number(status?.credit_limit ?? 0);
  const alreadyClaimed = Boolean(status?.claimed) || claimed;
  const canClaim =
    status?.status === "approved" && creditLimit > 0 && !alreadyClaimed;

  const handleClaim = async () => {
    if (!canClaim || claiming) return;
    setErrorMessage("");
    try {
      await claimCreditLimitAsync();
      setClaimed(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "We couldn't submit your claim. Please try again."
      );
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-3">
          <div className="h-6 w-56 bg-gray-200 rounded animate-pulse" />
          <div className="h-24 w-full bg-gray-100 rounded-xl animate-pulse" />
          <div className="h-12 w-full bg-gray-100 rounded-lg animate-pulse" />
        </div>
      );
    }

    if (isError) {
      return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-700 font-medium">
            We couldn't load your credit limit.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 rounded-lg bg-visaro-orange text-white px-5 py-2.5 font-medium hover:bg-orange-500 transition"
          >
            Retry
          </button>
        </div>
      );
    }

    // Successful claim (this session) or previously claimed.
    if (claimed || alreadyClaimed) {
      return (
        <div className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
            <CheckCircle size={38} weight="fill" />
          </div>
          <h2 className="text-xl font-semibold text-deepBlue mb-2">
            {claimed ? "Amount Claimed" : "Already Claimed"}
          </h2>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            Your loan request is awaiting partner approval. We'll update your
            loan status once a decision is available.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate("/payments/loans")}
              className="flex-1 rounded-lg bg-visaro-orange text-white py-3 font-medium hover:bg-orange-500 transition"
            >
              View Loans
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="flex-1 rounded-lg border border-gray-300 py-3 font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }

    // Pending eligibility review — nothing to claim yet.
    if (status?.status === "pending") {
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
            <Warning size={30} weight="fill" />
          </div>
          <h2 className="text-lg font-semibold text-deepBlue mb-1">
            Eligibility Under Review
          </h2>
          <p className="text-sm text-gray-600">
            Your credit limit hasn't been assigned yet. We'll email you as soon
            as it's ready to claim.
          </p>
        </div>
      );
    }

    // No approved credit limit available to claim.
    if (!canClaim) {
      return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center">
          <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center">
            <Warning size={30} weight="fill" />
          </div>
          <h2 className="text-lg font-semibold text-deepBlue mb-1">
            No Credit Limit to Claim
          </h2>
          <p className="text-sm text-gray-600">
            You don't have an approved credit limit available to claim right now.
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-5 rounded-lg border border-gray-300 px-5 py-2.5 font-medium text-gray-700 hover:bg-white transition"
          >
            Go Home
          </button>
        </div>
      );
    }

    // Approved, unclaimed — show the preview and claim CTA.
    return (
      <div>
        <div className="w-12 h-12 rounded-xl bg-visaro-orange/10 text-visaro-orange flex items-center justify-center mb-5">
          <ShieldCheck size={28} weight="fill" />
        </div>
        <h2 className="text-2xl font-semibold text-deepBlue">
          Claim your approved amount
        </h2>
        <p className="mt-2 text-sm text-gray-500 leading-6">
          Visaro has approved a credit amount for you. Claim it to send your loan
          request for final approval.
        </p>

        <div className="mt-6 rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex justify-between items-center gap-4 px-5 py-4 bg-gray-50">
            <span className="text-sm text-gray-500">Approved amount</span>
            <strong className="text-2xl text-deepBlue">
              {formatMoney(creditLimit)}
            </strong>
          </div>
          <p className="px-5 py-3 text-xs text-gray-500 leading-5">
            Claiming creates a loan request for this amount. There is no fee to
            claim &mdash; your eligibility has already been assessed.
          </p>
        </div>

        {errorMessage && (
          <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
            {errorMessage}
          </p>
        )}

        <button
          type="button"
          onClick={handleClaim}
          disabled={claiming}
          className="mt-7 w-full rounded-lg bg-visaro-orange text-white py-3 font-medium hover:bg-orange-500 transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {claiming && <Spinner size={17} className="animate-spin" />}
          {claiming ? "Claiming…" : "Claim Amount"}
        </button>
      </div>
    );
  };

  return (
    <div className="max-w-xl mx-auto px-5 py-10">
      <button
        onClick={() => navigate(-1)}
        type="button"
        className="flex items-center gap-2 text-deepBlue font-medium mb-6"
      >
        <CaretLeft /> <span>Back</span>
      </button>

      <div className="bg-white rounded-2xl drop-shadow-sm p-6 sm:p-8">
        {renderBody()}
      </div>
    </div>
  );
};

export default ClaimCreditLimit;
